import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  FormLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  TablePagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiService } from '../../utils/api';
import ViewerRegistrationForm from '../Auth/ViewerRegistrationForm';
import CorporateRegistrationForm from '../Auth/CorporateRegistrationForm';
import PosterRegistrationForm from '../Auth/PosterRegistrationForm';

// 定数としてのロール定義（表示名と実際のバックエンドのロール名のマッピング）
const USER_ROLES = {
  ADMIN: 'admin',
  CORPORATE: 'corporate',
  POSTER: 'poster',
  VIEWER: 'viewer', // viewerがバックエンドでは'user'かもしれない
  PREMIUM: 'premium',
  GUEST: 'guest'
};

// バックエンドのロール名へのマッピング
const FRONTEND_TO_BACKEND_ROLES = {
  'admin': 'admin',
  'corporate': 'corporate', 
  'poster': 'poster',
  'viewer': 'user', // viewerは実際にはuserとして保存されている可能性
  'premium': 'premium',
  'guest': 'guest'
};

// バックエンドからフロントエンドへのロール名マッピング
const BACKEND_TO_FRONTEND_ROLES = {
  'admin': 'admin',
  'corporate': 'corporate',
  'poster': 'poster',
  'user': 'viewer', // バックエンドの'user'はフロントエンドでは'viewer'
  'premium': 'premium',
  'guest': 'guest',
  'moderator': 'admin' // 'moderator'は'admin'として扱う
};

// ロール名の日本語表記マッピング
const ROLE_NAMES_JA = {
  [USER_ROLES.ADMIN]: '管理者',
  [USER_ROLES.CORPORATE]: '企業管理ユーザー',
  [USER_ROLES.POSTER]: '投稿ユーザー',
  [USER_ROLES.VIEWER]: '閲覧ユーザー',
  [USER_ROLES.PREMIUM]: 'プレミアム',
  [USER_ROLES.GUEST]: 'ゲスト'
};

// user 権限がまだ表示される場合の対応
const LEGACY_ROLE_MAPPING = {
  'user': 'viewer', // 存在しない user 権限は viewer として扱う
  'moderator': 'admin' // 存在しない moderator 権限は admin として扱う
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilterValue, setRoleFilterValue] = useState('all');
  const [statusFilterValue, setStatusFilterValue] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  
  // ロール変更ダイアログ用
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  
  // ユーザー編集ダイアログ用
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    display_name: '',
    email: '',
    role: '',
    bio: '',
    location: '',
    website: '',
    avatar: '',
    cover_image: '',
    is_hidden: false,
    is_locked: false,
    locked_reason: '',
    birth_date: '',
    gender: '',
    prefecture_code: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  
  // 登録フォームダイアログ用
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [registrationType, setRegistrationType] = useState('');
  
  // 削除確認ダイアログ用
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // 都道府県データ用
  const [prefectures, setPrefectures] = useState([]);

  // 電話番号ブロック履歴用
  const [phoneBlocks, setPhoneBlocks] = useState([]);
  const [phoneBlocksLoading, setPhoneBlocksLoading] = useState(false);

  // 電話番号ブロック履歴を取得
  const fetchPhoneBlocks = async () => {
    try {
      setPhoneBlocksLoading(true);
      const response = await apiService.get(`/api/auth/admin/phone-blocks?_t=${Date.now()}`);
      setPhoneBlocks((response.data && response.data.blocks) || []);
    } catch (err) {
      setPhoneBlocks([]);
    } finally {
      setPhoneBlocksLoading(false);
    }
  };

  // ブロック解除
  const handleReleasePhoneBlock = async (blockId) => {
    if (!window.confirm('この電話番号のブロックを解除しますか？解除すると、その番号で新規登録が可能になります。')) {
      return;
    }
    try {
      await apiService.post(`/api/auth/admin/phone-blocks/${blockId}/release`);
      setSuccessMessage('電話番号のブロックを解除しました');
      fetchPhoneBlocks();
    } catch (err) {
      setError('ブロック解除に失敗しました');
    }
  };
  
  // 都道府県データの取得
  const fetchPrefectures = async () => {
    try {
      const response = await apiService.get('/api/areas/prefectures');
      setPrefectures(response.data);
    } catch (error) {
      
    }
  };
  
  // ユーザーデータの読み込み
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      // 管理者用のユーザー一覧APIを使用（キャッシュを無効化）
      const timestamp = new Date().getTime();
      const response = await apiService.get(`/api/auth/admin/users?_t=${timestamp}&page=${page + 1}&per_page=${rowsPerPage}`);
      
      if (response.data && response.data.users) {
        const usersList = response.data.users;
        
        // ページネーション情報を設定
        if (response.data.pagination) {
          setTotalUsers(response.data.pagination.total);
          setTotalPages(response.data.pagination.pages);
        }
        
        // 各ユーザーの詳細情報を取得
        const usersWithDetails = await Promise.all(
          usersList.map(async (user) => {
            try {
              // 拡張プロフィール情報（bio, location, website）を取得
              const userDetailResponse = await apiService.get(`/api/profiles/${user.username}`, {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
                },
                // キャッシュ回避
                params: {
                  _t: timestamp
                },
                // タイムアウト設定を延長
                timeout: 10000
              });
              
              // ロール情報が文字列の場合はオブジェクトに変換
              let roleName = user.role;
              let roleInfo;
              if (typeof roleName === 'string') {
                // 存在しない古い権限を正しい権限にマッピング
                if (LEGACY_ROLE_MAPPING[roleName]) {
                  roleName = LEGACY_ROLE_MAPPING[roleName];
                }
                roleInfo = { name: roleName };
              } else {
                roleInfo = user.role;
              }
              
              // 取得した詳細情報とマージ
              return {
                ...user,
                ...userDetailResponse.data,  // プロフィール情報を追加（bio, location, website, cover_image等）
                role: roleInfo,
                prefecture_code: userDetailResponse.data.prefecture_code || user.prefecture_code || null,
                prefecture: userDetailResponse.data.prefecture || user.prefecture || null
              };
            } catch (detailError) {
              // HTMLレスポンスエラーの場合は特別に処理
              if (detailError.message && (
                  detailError.message.includes('HTML形式の応答') || 
                  detailError.message.includes('内容がJSONではありません')
              )) {
                // スキップして基本情報だけを返す
              }
              
              // エラーの場合はユーザーの基本情報と最小限のロール情報を返す
              let roleName = user.role;
              let roleInfo;
              if (typeof roleName === 'string') {
                // 存在しない古い権限を正しい権限にマッピング
                if (LEGACY_ROLE_MAPPING[roleName]) {
                  roleName = LEGACY_ROLE_MAPPING[roleName];
                }
                roleInfo = { name: roleName };
              } else {
                roleInfo = user.role || { name: 'viewer' };
              }
              
              // 基本ユーザー情報を返す（詳細なし）
              return {
                ...user,
                role: roleInfo,
                profileError: true, // プロフィール取得エラーフラグ
                prefecture_code: user.prefecture_code || null
              };
            }
          })
        );
        
        setUsers(usersWithDetails);
      } else {
        setError('ユーザーデータの形式が不正です');
      }
    } catch (error) {
      setError('ユーザーデータの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUsers();
    fetchPrefectures(); // 都道府県データを読み込む
  }, [page, rowsPerPage]);
  
  // タブの切り替え
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // 電話番号ブロック履歴タブ（index 2）を開いたら取得
    if (newValue === 2) {
      fetchPhoneBlocks();
    }
  };
  
  // 検索語の変更
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // ロールフィルターの変更
  const handleRoleFilterChange = (event) => {
    setRoleFilterValue(event.target.value);
  };
  
  // 状態フィルターの変更
  const handleStatusFilterChange = (event) => {
    setStatusFilterValue(event.target.value);
  };
  
  // ユーザー情報の再読み込み
  const handleRefresh = () => {
    fetchUsers();
  };
  
  // ユーザーのフィルタリング
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (user.display_name && user.display_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // ロールマッピングを考慮したフィルタリング
    const userRoleName = user.role && user.role.name ? user.role.name : '';
    const mappedRoleName = LEGACY_ROLE_MAPPING[userRoleName] || userRoleName;
    const matchesRole = roleFilterValue === 'all' || mappedRoleName === roleFilterValue;
    
    let matchesStatus = true;
    if (statusFilterValue === 'locked') {
      matchesStatus = user.is_locked === true;
    } else if (statusFilterValue === 'hidden') {
      matchesStatus = user.is_hidden === true;
    } else if (statusFilterValue === 'active') {
      matchesStatus = user.is_locked !== true && user.is_hidden !== true;
    }
    
    return matchesSearch && matchesRole && matchesStatus;
  });
  
  // ロール変更ダイアログを開く
  const openChangeRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role ? user.role.name : 'viewer'); // デフォルトで閲覧ユーザーを選択
    setOpenRoleDialog(true);
  };
  
  // ロール変更の実行
  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) return;
    
    setRoleChangeLoading(true);
    setError(''); // エラーをリセット
    
    try {
      // ロール名をバックエンド用に変換
      let backendRole = FRONTEND_TO_BACKEND_ROLES[newRole] || newRole;
      
      // リクエスト設定
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Accept': 'application/json'
        }
      };
      
      // ロール変更は user_id と role を含むデータを送信
      const updateData = { role: backendRole }; // 変換後のロール名を使用
      
      // admin_update_userエンドポイントを使用（PUTメソッド）
      const response = await apiService.put(`/api/auth/admin/users/${selectedUser.id}`, updateData, config);
      
      // ユーザーリストを更新
      setUsers(users.map(user => {
        if (user.id === selectedUser.id) {
          return {
            ...user,
            role: { ...user.role, name: backendRole }
          };
        }
        return user;
      }));
      
      // 成功メッセージを表示
      setSuccessMessage(`「${selectedUser.username}」のロールを「${ROLE_NAMES_JA[backendRole] || backendRole}」に変更しました`);
      
      // ダイアログを閉じる
      setOpenRoleDialog(false);
      setSelectedUser(null);
      setNewRole('');
    } catch (error) {
      
      
      setError(`ロールの変更に失敗しました: ${error.response?.data?.message || error.message || 'APIエラー'}`);
    } finally {
      setRoleChangeLoading(false);
    }
  };
  
  // ロール名のマッピング
  const getRoleName = (role, user) => {
    if (!role) {
      return '未設定';
    }
    
    // roleが文字列の場合
    if (typeof role === 'string') {
      // バックエンドのロール名をフロントエンドのロール名に変換
      const frontendRole = BACKEND_TO_FRONTEND_ROLES[role.toLowerCase()] || role.toLowerCase();
      return ROLE_NAMES_JA[frontendRole] || frontendRole;
    }
    
    // roleがオブジェクトの場合
    if (typeof role === 'object' && role !== null) {
      // role.nameプロパティがある場合
      if (role.name) {
        const frontendRole = BACKEND_TO_FRONTEND_ROLES[role.name.toLowerCase()] || role.name.toLowerCase();
        return ROLE_NAMES_JA[frontendRole] || frontendRole;
      }
      
      // role.roleプロパティがある場合
      if (role.role) {
        if (typeof role.role === 'string') {
          const frontendRole = BACKEND_TO_FRONTEND_ROLES[role.role.toLowerCase()] || role.role.toLowerCase();
          return ROLE_NAMES_JA[frontendRole] || frontendRole;
        } else if (typeof role.role === 'object' && role.role !== null && role.role.name) {
          const frontendRole = BACKEND_TO_FRONTEND_ROLES[role.role.name.toLowerCase()] || role.role.name.toLowerCase();
          return ROLE_NAMES_JA[frontendRole] || frontendRole;
        }
      }
      
      // roleにtype, role_id, idなどの属性がある場合
      if (role.type) {
        const frontendRole = BACKEND_TO_FRONTEND_ROLES[role.type.toLowerCase()] || role.type.toLowerCase();
        return ROLE_NAMES_JA[frontendRole] || frontendRole;
      }
      
      // すべてのプロパティを調べる
      const roleKeys = Object.keys(role);
      for (const key of roleKeys) {
        const value = role[key];
        if (typeof value === 'string') {
          // 文字列値がロール名と一致するか確認
          const frontendValue = BACKEND_TO_FRONTEND_ROLES[value.toLowerCase()] || value.toLowerCase();
          if (ROLE_NAMES_JA[frontendValue]) {
            return ROLE_NAMES_JA[frontendValue];
          }
        }
      }
    }
    
    return '未設定';
  };
  
  // 各種登録フォームを開く
  const openRegistrationForm = (type, user = null) => {
    if (type === 'corporate') {
      navigate('/corporate-register');
      return;
    } else if (type === 'poster') {
      navigate('/poster-register');
      return;
    }
    
    setSelectedUser(user);
    setRegistrationType(type);
    setOpenRegisterDialog(true);
  };
  
  // ユーザー編集ダイアログを開く
  const openUserEditDialog = (user) => {
    // ユーザー情報をフォームに設定
    setEditUser(user);
    
    setEditUserForm({
      username: user.username || '',
      display_name: user.display_name || '',
      email: user.email || '',
      role: user.role?.name || '',
      bio: user.bio || '',
      location: user.location || '',
      website: user.website || '',
      avatar: user.avatar || '',
      cover_image: user.cover_image || '',
      is_hidden: user.is_hidden || false,
      is_locked: user.is_locked || false,
      locked_reason: user.locked_reason || '',
      birth_date: user.birth_date || '',
      gender: user.gender || '',
      prefecture_code: user.prefecture_code || ''
    });
    
    // ダイアログを開く
    setOpenEditDialog(true);
  };
  
  // 編集フォームの変更ハンドラ
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditUserForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // ユーザー情報更新の実行
  const handleUserUpdate = async () => {
    setEditLoading(true);
    setError('');
    
    try {
      // 編集内容をAPI経由で保存
      const userData = {
        ...editUserForm
      };
      
      // ユーザー情報の更新
      const response = await apiService.put(`/api/auth/admin/users/${editUser.id}`, userData);
      
      // 更新成功メッセージ
      setSuccessMessage(`ユーザー ${editUserForm.username} の情報を更新しました`);
      
      // ダイアログを閉じる
      setOpenEditDialog(false);
      
      // ユーザーリストを再読み込み
      fetchUsers();
    } catch (error) {
      setError(`ユーザー情報の更新に失敗しました: ${error.response?.data?.error || error.message}`);
    } finally {
      setEditLoading(false);
    }
  };
  
  // ユーザー登録完了のコールバック
  const handleRegistrationComplete = (newUser) => {
    setUsers(prevUsers => [...prevUsers, newUser]);
    setOpenRegisterDialog(false);
  };

  // ユーザー削除ダイアログを開く
  const openDeleteUserDialog = (user) => {
    setDeleteUser(user);
    setOpenDeleteDialog(true);
  };
  
  // ユーザー削除処理
  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    
    setDeleteLoading(true);
    setError(''); // エラーをリセット
    
    try {
      // リクエスト設定
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Accept': 'application/json'
        }
      };
      
      // 削除操作は実際にDELETEメソッドを使用してユーザーを物理的に削除する
      const response = await apiService.delete(`/api/auth/admin/users/${deleteUser.id}`, config);
      
      // ユーザーリストを更新
      setUsers(users.filter(user => user.id !== deleteUser.id));
      
      // 成功メッセージを表示
      setSuccessMessage(`ユーザー「${deleteUser.username}」を完全に削除しました`);
      
      // ダイアログを閉じる
      setOpenDeleteDialog(false);
      setDeleteUser(null);
    } catch (error) {
      
      
      setError(`ユーザー削除エラー: ${error.response?.data?.message || error.message || 'APIエラー'}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // ページネーションハンドラーを追加
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // 登録フォームのレンダリング
  const renderRegistrationContent = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          新規ユーザー登録
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/register')}
            sx={{ mr: 2, mb: 1 }}
          >
            閲覧ユーザー登録
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/corporate-register')}
            sx={{ mr: 2, mb: 1 }}
          >
            企業ユーザー登録
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/poster-register')}
            sx={{ mb: 1 }}
          >
            投稿ユーザー登録
          </Button>
        </Box>
      </Box>
    );
  };
  
  // 電話番号ブロック履歴のレンダリング
  const renderPhoneBlocksContent = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            電話番号の変更・ブロック履歴（変更前の番号は2年間、新規登録に使用できません）
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchPhoneBlocks}>
            更新
          </Button>
        </Box>
        {phoneBlocksLoading ? (
          <Typography sx={{ p: 2 }}>読み込み中...</Typography>
        ) : phoneBlocks.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            電話番号の変更・ブロック履歴はありません。
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table aria-label="電話番号ブロック履歴">
              <TableHead>
                <TableRow>
                  <TableCell>旧電話番号</TableCell>
                  <TableCell>新電話番号</TableCell>
                  <TableCell>ユーザー名</TableCell>
                  <TableCell>変更日時</TableCell>
                  <TableCell>ブロック期限</TableCell>
                  <TableCell>状態</TableCell>
                  <TableCell>アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phoneBlocks.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.phone}</TableCell>
                    <TableCell>{b.new_phone}</TableCell>
                    <TableCell>{b.username}</TableCell>
                    <TableCell>{b.changed_at ? String(b.changed_at).slice(0, 19).replace('T', ' ') : ''}</TableCell>
                    <TableCell>{b.blocked_until ? String(b.blocked_until).slice(0, 10) : ''}</TableCell>
                    <TableCell>
                      {b.released ? '解除済み' : b.is_active ? 'ブロック中' : '期限切れ'}
                    </TableCell>
                    <TableCell>
                      {!b.released && b.is_active && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleReleasePhoneBlock(b.id)}
                        >
                          ブロック解除
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // renderContent関数を変更して、ページネーションコンポーネントを追加
  const renderContent = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          ユーザー管理
        </Typography>
        
        {/* エラーメッセージ表示 */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        
        {/* 成功メッセージ表示 */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
        
        <Paper sx={{ p: 2, mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="ユーザー一覧" />
            <Tab label="登録フォーム" />
          </Tabs>
          
          {tabValue === 0 && (
            <Box>
              <Box sx={{ display: 'flex', mb: 2, gap: 2 }}>
                <TextField
                  placeholder="ユーザー名で検索"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="role-filter-label">ロール</InputLabel>
                  <Select
                    labelId="role-filter-label"
                    id="role-filter"
                    value={roleFilterValue}
                    label="ロール"
                    onChange={handleRoleFilterChange}
                  >
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="admin">管理者</MenuItem>
                    <MenuItem value="corporate">企業</MenuItem>
                    <MenuItem value="poster">投稿者</MenuItem>
                    <MenuItem value="viewer">閲覧者</MenuItem>
                    <MenuItem value="premium">プレミアム</MenuItem>
                    <MenuItem value="guest">ゲスト</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="status-filter-label">状態</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilterValue}
                    label="状態"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="active">有効</MenuItem>
                    <MenuItem value="locked">ロック中</MenuItem>
                    <MenuItem value="hidden">非表示</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  更新
                </Button>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table aria-label="ユーザー一覧">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>ユーザー名</TableCell>
                          <TableCell>表示名</TableCell>
                          <TableCell>メール</TableCell>
                          <TableCell>ロール</TableCell>
                          <TableCell>状態</TableCell>
                          <TableCell>アクション</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.id}</TableCell>
                            <TableCell>{user.username}</TableCell>
                            <TableCell>{user.display_name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{getRoleName(user.role, user)}</TableCell>
                            <TableCell>
                              {user.is_locked && (
                                <Box component="span" sx={{ display: 'inline-block', mr: 1 }}>
                                  <Box
                                    component="span"
                                    sx={{
                                      backgroundColor: 'error.light',
                                      color: 'white',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    ロック中
                                  </Box>
                                </Box>
                              )}
                              {user.is_hidden && (
                                <Box component="span" sx={{ display: 'inline-block' }}>
                                  <Box
                                    component="span"
                                    sx={{
                                      backgroundColor: 'info.light',
                                      color: 'white',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    非表示
                                  </Box>
                                </Box>
                              )}
                              {!user.is_locked && !user.is_hidden && (
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: 'success.light',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  有効
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button 
                                size="small" 
                                variant="contained"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={() => openUserEditDialog(user)}
                                sx={{ mr: 1 }}
                              >
                                編集
                              </Button>
                              <Button 
                                size="small" 
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => openDeleteUserDialog(user)}
                              >
                                削除
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="表示件数:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`}
                  />
                </>
              )}
            </Box>
          )}
          
          {tabValue === 1 && renderRegistrationContent()}

        </Paper>
        
        {/* ロール変更ダイアログ */}
        <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)}>
          <DialogTitle>ユーザーロールの変更</DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Box sx={{ minWidth: 300, pt: 1 }}>
                <Typography variant="body1" gutterBottom>
                  ユーザー: {selectedUser.username}
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="new-role-label">新しいロール</InputLabel>
                  <Select
                    labelId="new-role-label"
                    value={newRole}
                    label="新しいロール"
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <MenuItem value="admin">管理者</MenuItem>
                    <MenuItem value="corporate">企業管理ユーザー</MenuItem>
                    <MenuItem value="poster">投稿ユーザー</MenuItem>
                    <MenuItem value="viewer">閲覧ユーザー</MenuItem>
                    <MenuItem value="premium">プレミアム</MenuItem>
                    <MenuItem value="guest">ゲスト</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRoleDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={handleRoleChange} 
              variant="contained" 
              color="primary"
              disabled={roleChangeLoading}
            >
              {roleChangeLoading ? <CircularProgress size={24} /> : '変更'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* ユーザー編集ダイアログ */}
        <Dialog 
          open={openEditDialog} 
          onClose={() => setOpenEditDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>ユーザー情報の編集</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ユーザー名"
                  name="username"
                  value={editUserForm.username}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                  disabled
                  helperText="ユーザー名は変更できません"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="表示名"
                  name="display_name"
                  value={editUserForm.display_name}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="メールアドレス"
                  name="email"
                  value={editUserForm.email}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="role-label">権限</InputLabel>
                  <Select
                    labelId="role-label"
                    name="role"
                    value={editUserForm.role}
                    onChange={handleEditFormChange}
                    label="権限"
                  >
                    {Object.entries(ROLE_NAMES_JA).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="自己紹介"
                  name="bio"
                  value={editUserForm.bio}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="場所"
                  name="location"
                  value={editUserForm.location}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Webサイト"
                  name="website"
                  value={editUserForm.website}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="アバターURL"
                  name="avatar"
                  value={editUserForm.avatar}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="カバー画像URL"
                  name="cover_image"
                  value={editUserForm.cover_image}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="prefecture-label">都道府県</InputLabel>
                  <Select
                    labelId="prefecture-label"
                    name="prefecture_code"
                    value={editUserForm.prefecture_code || ''}
                    onChange={handleEditFormChange}
                    label="都道府県"
                  >
                    <MenuItem value="">
                      <em>選択なし</em>
                    </MenuItem>
                    {prefectures.map((pref) => (
                      <MenuItem key={pref.code} value={pref.code}>
                        {pref.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>ユーザーの都道府県設定</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="生年月日"
                  name="birth_date"
                  type="date"
                  value={editUserForm.birth_date || ''}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">性別</FormLabel>
                  <RadioGroup
                    row
                    name="gender"
                    value={editUserForm.gender || ''}
                    onChange={handleEditFormChange}
                  >
                    <FormControlLabel value="male" control={<Radio />} label="男性" />
                    <FormControlLabel value="female" control={<Radio />} label="女性" />
                    <FormControlLabel value="other" control={<Radio />} label="その他" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUserForm.is_hidden}
                      onChange={(e) => setEditUserForm({...editUserForm, is_hidden: e.target.checked})}
                      name="is_hidden"
                      color="primary"
                    />
                  }
                  label="ユーザーを非表示にする"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editUserForm.is_locked}
                      onChange={(e) => setEditUserForm({...editUserForm, is_locked: e.target.checked})}
                      name="is_locked"
                      color="primary"
                    />
                  }
                  label="アカウントをロックする"
                />
              </Grid>
              {editUserForm.is_locked && (
                <Grid item xs={12}>
                  <TextField
                    label="ロック理由"
                    name="locked_reason"
                    value={editUserForm.locked_reason}
                    onChange={handleEditFormChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)} color="inherit">
              キャンセル
            </Button>
            <Button 
              onClick={handleUserUpdate} 
              color="primary" 
              variant="contained"
              disabled={editLoading}
            >
              {editLoading ? <CircularProgress size={24} /> : '保存'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* 登録フォームダイアログ */}
        <Dialog
          open={openRegisterDialog} 
          onClose={() => setOpenRegisterDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {registrationType === 'viewer' && '閲覧ユーザー登録'}
          </DialogTitle>
          <DialogContent>
            {registrationType === 'viewer' && <ViewerRegistrationForm onComplete={handleRegistrationComplete} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRegisterDialog(false)} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* ユーザー削除確認ダイアログ */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>ユーザーの完全削除</DialogTitle>
          <DialogContent>
            {deleteUser && (
              <DialogContentText>
                ユーザー「{deleteUser.username}」をデータベースから完全に削除します。このユーザーに関連するすべてのデータ（投稿、コメント、いいね、ブックマークなど）も削除されます。この操作は元に戻せません。本当に削除しますか？
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              variant="contained" 
              color="error"
              disabled={deleteLoading}
            >
              {deleteLoading ? <CircularProgress size={24} /> : '完全に削除'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>ユーザー管理</Typography>
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="ユーザー一覧" />
        <Tab label="新規登録" />
        <Tab label="電話番号ブロック履歴" />
      </Tabs>
      
      {tabValue === 0 && renderContent()}
      {tabValue === 1 && renderRegistrationContent()}
      {tabValue === 2 && renderPhoneBlocksContent()}
    </Box>
  );
};

export default UserManagement; 